import { useState, useEffect, useCallback, useRef, memo } from "react";
import { Box, Typography, Card, CardContent, Chip, Stack } from "@mui/material";
import {
  Person as PersonIcon,
  Verified as VerifiedIcon,
  RestartAlt as ResetIcon,
} from "@mui/icons-material";
import FilterGroup from "./FilterGroup";
import RecipientsPreview from "./RecipientsPreview";
import { ITS_COLUMNS, type FilterGroupData } from "./constants";

type AdvancedFilterBuilderProps = {
  value: FilterGroupData | undefined | null;
  onChange?: (group: FilterGroupData) => void;
  onPreviewCount?: (count: number) => void;
  onSelectionChange?: (
    recipients: { ITS_ID: string | null; phone: string; Full_Name: string | null }[]
  ) => void;
  onPreviewed?: (previewed: boolean) => void;
};

const AdvancedFilterBuilder = memo(
  ({
    value,
    onChange,
    onPreviewCount,
    onSelectionChange,
    onPreviewed,
  }: AdvancedFilterBuilderProps) => {
    const getDefaultGroup = useCallback(
      (): FilterGroupData => ({
        id: "root",
        logic: "AND",
        rules: [],
        groups: [],
      }),
      []
    );

    const [filterGroup, setFilterGroup] = useState<FilterGroupData>(value || getDefaultGroup());

    const skippedIdsMapRef = useRef(new Map<string, Set<string>>());
    const shouldFetchMapRef = useRef(new Map<string, boolean>());

    const [skippedIdsMap, setSkippedIdsMap] = useState(skippedIdsMapRef.current);
    const [shouldFetchMap, setShouldFetchMap] = useState(shouldFetchMapRef.current);

    const updateSkippedIdsMap = useCallback(
      (updater: (prev: Map<string, Set<string>>) => Map<string, Set<string>>) => {
        setSkippedIdsMap((prev) => {
          const newMap = updater(prev);
          skippedIdsMapRef.current = newMap;
          return newMap;
        });
      },
      []
    );

    const updateShouldFetchMap = useCallback(
      (updater: (prev: Map<string, boolean>) => Map<string, boolean>) => {
        setShouldFetchMap((prev) => {
          const newMap = updater(prev);
          shouldFetchMapRef.current = newMap;
          return newMap;
        });
      },
      []
    );

    useEffect(() => {
      if (value) {
        setFilterGroup(value);
      } else {
        setFilterGroup(getDefaultGroup());
      }
    }, [value, getDefaultGroup]);

    const handleFilterChange = useCallback(
      (updatedGroup: FilterGroupData) => {
        setFilterGroup(updatedGroup);
        onChange?.(updatedGroup);
      },
      [onChange]
    );

    const handleReset = useCallback(() => {
      handleFilterChange({
        id: "root",
        logic: "AND",
        rules: [],
        groups: [],
      });
    }, [handleFilterChange]);

    const applyPreset = useCallback(
      (presetKey: "hof" | "misaq" | "clear") => {
        if (presetKey === "clear") {
          handleReset();
          return;
        }
        let rules = [...(filterGroup.rules || [])];
        if (presetKey === "hof") {
          rules = rules.filter((r) => r.field !== "HOF_FM_TYPE");
          rules.push({
            id: `rule_${Date.now()}_hof`,
            field: "HOF_FM_TYPE",
            operator: "equals",
            value: "HOF",
          });
        } else if (presetKey === "misaq") {
          rules = rules.filter((r) => r.field !== "Misaq");
          rules.push({
            id: `rule_${Date.now()}_misaq`,
            field: "Misaq",
            operator: "equals",
            value: "Done",
          });
        }
        handleFilterChange({
          ...filterGroup,
          rules,
        });
      },
      [filterGroup, handleFilterChange, handleReset]
    );

    const totalRules = (filterGroup.rules?.length || 0) + (filterGroup.groups?.length || 0);

    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {/* Header & Quick Presets */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
              Filter Conditions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Build nested rules with AND/OR logic to target specific recipients
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}
          >
            <Chip
              icon={<PersonIcon sx={{ fontSize: 16 }} />}
              label="HOF Only"
              size="small"
              variant="outlined"
              onClick={() => applyPreset("hof")}
              sx={{
                fontSize: "0.75rem",
                cursor: "pointer",
                fontWeight: 600,
                height: 28,
                px: 0.75,
                borderRadius: 1.5,
                transition: "all 0.15s ease",
                "& .MuiChip-label": { px: 1 },
                "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
              }}
            />
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
              label="Misaaq Done"
              size="small"
              variant="outlined"
              onClick={() => applyPreset("misaq")}
              sx={{
                fontSize: "0.75rem",
                cursor: "pointer",
                fontWeight: 600,
                height: 28,
                px: 0.75,
                borderRadius: 1.5,
                transition: "all 0.15s ease",
                "& .MuiChip-label": { px: 1 },
                "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
              }}
            />
            {totalRules > 0 && (
              <Chip
                icon={<ResetIcon sx={{ fontSize: 16 }} />}
                label="Reset"
                size="small"
                color="error"
                variant="outlined"
                onClick={() => applyPreset("clear")}
                sx={{
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  fontWeight: 600,
                  height: 28,
                  px: 0.75,
                  borderRadius: 1.5,
                  transition: "all 0.15s ease",
                  "& .MuiChip-label": { px: 1 },
                  "&:hover": { bgcolor: "error.lighter" },
                }}
              />
            )}
          </Stack>
        </Box>

        {/* Rule Builder Container */}
        <FilterGroup
          group={filterGroup}
          onUpdate={handleFilterChange}
          onDelete={handleReset}
          level={0}
          availableColumns={ITS_COLUMNS}
        />

        {/* Live Preview Card */}
        <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Audience Preview & Verification
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
              Preview and verify the matched recipients before proceeding to template mapping
            </Typography>

            <RecipientsPreview
              filterGroup={filterGroup}
              onPreviewCount={onPreviewCount}
              onSelectionChange={onSelectionChange}
              onPreviewed={onPreviewed}
              skippedIdsMap={skippedIdsMap}
              setSkippedIdsMap={updateSkippedIdsMap}
              shouldFetchMap={shouldFetchMap}
              setShouldFetchMap={updateShouldFetchMap}
            />
          </CardContent>
        </Card>
      </Box>
    );
  }
);

AdvancedFilterBuilder.displayName = "AdvancedFilterBuilder";

export default AdvancedFilterBuilder;
